import {
  Bell,
  ChevronDown,
  ClipboardList,
  Globe,
  IdCard,
  LayoutDashboard,
  LogOut,
  UserRound,
} from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, redirect, useLoaderData, useNavigate } from 'react-router';

import { Layout as SharedLayout } from '@cold-ambulance/layout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cold-ambulance/shadcn';
import {
  AcceptTermsDialog,
  logout,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import { requiresAuth } from './auth-loader.ts';
import { auth, useUser } from './firebase.tsx';

function DefaultLogo(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <circle cx="22" cy="22" r="22" fill="#337540" />
      <path
        d="M29.2871 18.3014C28.4148 18.2837 27.5652 18.58 26.8931 19.1363C26.221 19.6925 25.7711 20.4717 25.6253 21.3318C25.4795 22.192 25.6476 23.0759 26.0989 23.8225C26.5501 24.5692 27.2546 25.1289 28.084 25.3996V28.7081C28.084 29.8249 27.6403 30.896 26.8506 31.6857C26.061 32.4753 24.9899 32.919 23.8731 32.919C22.7563 32.919 21.6853 32.4753 20.8956 31.6857C20.1059 30.896 19.6623 29.8249 19.6623 28.7081V23.8958C21.4316 23.6163 23.0431 22.7144 24.2066 21.3525C25.37 19.9905 26.0091 18.2579 26.0086 16.4666V10.9625H23.2114V10H20.8052V14.3311H23.2114V13.3687H23.6325V16.4666C23.5338 17.7511 22.9538 18.951 22.0086 19.8263C21.0634 20.7016 19.8226 21.1879 18.5344 21.1879C17.2461 21.1879 16.0053 20.7016 15.0601 19.8263C14.115 18.951 13.535 17.7511 13.4363 16.4666V13.3687H13.8574V14.3311H16.2635V10H13.8574V10.9625H11V16.4666C11.0032 18.2396 11.6327 19.9543 12.7774 21.3082C13.9221 22.662 15.5084 23.5678 17.2561 23.8657V28.7081C17.2561 30.4631 17.9532 32.1462 19.1942 33.3871C20.4351 34.628 22.1182 35.3252 23.8731 35.3252C25.6281 35.3252 27.3111 34.628 28.5521 33.3871C29.793 32.1462 30.4902 30.4631 30.4902 28.7081V25.3996C31.3195 25.1289 32.024 24.5692 32.4753 23.8225C32.9265 23.0759 33.0946 22.192 32.9488 21.3318C32.803 20.4717 32.3531 19.6925 31.681 19.1363C31.0089 18.58 30.1593 18.2837 29.2871 18.3014ZM29.2871 23.204C29.0432 23.204 28.8047 23.1317 28.6019 22.9962C28.3992 22.8606 28.2411 22.6681 28.1478 22.4427C28.0544 22.2174 28.03 21.9694 28.0776 21.7302C28.1252 21.491 28.2426 21.2713 28.4151 21.0988C28.5875 20.9264 28.8073 20.8089 29.0465 20.7613C29.2857 20.7137 29.5336 20.7382 29.759 20.8315C29.9843 20.9248 30.1769 21.0829 30.3124 21.2857C30.4479 21.4885 30.5202 21.7269 30.5202 21.9708C30.5202 22.2979 30.3903 22.6115 30.159 22.8428C29.9278 23.0741 29.6141 23.204 29.2871 23.204Z"
        fill="white"
      />
    </svg>
  );
}

function CompanyLogo({
  logoUrl,
  className,
}: {
  logoUrl?: string | null;
  className?: string;
}) {
  const classes = ['rounded-full object-cover', className]
    .filter(Boolean)
    .join(' ');
  if (logoUrl) {
    return <img src={logoUrl} alt="logo" className={classes} loading="lazy" />;
  }
  return <DefaultLogo className={className} />;
}

function NotificationBell() {
  const { t } = useTranslation();
  const notificationsQuery = useData('GET /notifications', {
    read: 'false',
    page: 1,
    pageSize: 5,
  });

  const markAsReadAction = useAction('PUT /notifications/{id}/read', {
    invalidate: ['GET /notifications'],
  });

  const unreadCount = notificationsQuery.data?.totalCount ?? 0;
  const notifications = notificationsQuery.data?.records ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="relative p-2"
        aria-label={t('header.notifications')}
      >
        <Bell size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -start-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <div className="border-b px-4 py-2 font-bold">
          {t('header.notifications')}
        </div>
        {notifications.length === 0 ? (
          <div className="px-4 py-6 text-center text-gray-500">
            {t('header.noNotifications')}
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                asChild
                className="cursor-pointer"
                onClick={() => markAsReadAction.mutate({ id: notification.id })}
              >
                <Link
                  to={notification.actionUrl || '/'}
                  className="flex flex-col items-end gap-1 px-4 py-3 text-end hover:bg-gray-50"
                >
                  <p className="text-sm font-semibold">{notification.title}</p>
                  <p className="line-clamp-2 text-end text-xs text-gray-600">
                    {notification.message}
                  </p>
                </Link>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.lang = newLang;
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  return (
    <button
      onClick={toggleLanguage}
      className="flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
    >
      <Globe size={16} />
      {i18n.language === 'ar' ? 'EN' : 'عربي'}
    </button>
  );
}

function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useLoaderData() as Awaited<ReturnType<typeof layoutLoader>>;

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  return (
    <div className="page-shadow flex items-center justify-between rounded-lg bg-white px-12 py-6">
      <p>
        <span>{t('header.welcome')}</span>
        <span> </span>
        <span className="font-bold">{user.name}</span>
      </p>
      <div className="flex items-center gap-4">
        <LanguageSwitcher />
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2">
            <CompanyLogo className="size-8" />
            <p>{user.name}</p>
            <ChevronDown className="mt-1 size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem asChild className="justify-end py-2">
              <div className="justify-end py-2">
                <Link to="/profile">{t('header.profileData')}</Link>
                <UserRound className="text-black" />
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="justify-end py-2"
              onClick={() =>
                logout(auth).then(() => navigate('/signin', { replace: true }))
              }
            >
              {t('header.logout')}
              <LogOut className="text-black" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function TermsGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userQuery = useUser();
  const acceptTermsAction = useAction('POST /vendor/me/accept-terms', {
    invalidate: ['GET /vendor/me'],
    meta: { toast: true },
  });

  return (
    <AcceptTermsDialog
      open={!!userQuery.data && !userQuery.data.termsAcceptedAt}
      isPending={acceptTermsAction.isPending}
      onAccept={() => acceptTermsAction.mutate({})}
      onLogout={() =>
        logout(auth).then(() => navigate('/signin', { replace: true }))
      }
      documents={[
        {
          href: 'https://medhub.tgl.com.sa/vendor-agreement',
          label: t('signup.vendorAgreement'),
          kind: 'agreement',
        },
        {
          href: 'https://medhub.tgl.com.sa/terms-and-privacy',
          label: t('signup.privacyPolicy'),
          kind: 'privacy',
        },
      ]}
    />
  );
}

export default function Layout() {
  const { t } = useTranslation();
  return (
    <SharedLayout
      items={[
        {
          href: '/',
          icon: <LayoutDashboard strokeWidth={2} />,
          label: t('nav.dashboard'),
        },
        {
          href: '/requests',
          icon: <ClipboardList strokeWidth={2} />,
          label: t('nav.requests'),
        },
        {
          href: '/offers',
          icon: <IdCard strokeWidth={2} />,
          label: t('nav.sentOffers'),
        },
        {
          href: '/profile',
          icon: <UserRound strokeWidth={2} />,
          label: t('nav.profile'),
        },
      ]}
    >
      <Header />
      <TermsGate />
    </SharedLayout>
  );
}

export async function layoutLoader() {
  const identity = await requiresAuth();
  if (!identity.contactId?.trim() || !identity.companyDocumentsId?.trim()) {
    throw redirect('/signup');
  }
  return identity;
}
